import { yupResolver } from '@hookform/resolvers/yup'
import { addDays } from '@if/core/date'
import { EFinancingProgram, ELoanPurpose, EOriginSystemId, EPaymentPlan, EProvince } from '@if/core/types'
import { formatCurrency, formatDate, normalizeNumber } from '@if/core/utils'
import { ControlledCheckbox, ControlledNumberField, FieldReadyGateProvider } from '@if/mui'
import { Add as AddIcon, Alarm, Info as InfoIcon, Lock } from '@mui/icons-material'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  debounce,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import AsyncActionButton from '@src/components/AsyncActionButton'
import PageSpinner from '@src/components/PageSpinner'
import SelectMerchantsDialog from '@src/components/SelectMerchantsDialog'
import ConflictDialog from '@src/containers/App/components/ConflictDialog'
import { useMerchantFinancingConfig } from '@src/data/api/configs-api/financing-config-api'
import { useGetFirstPaymentDateOptions } from '@src/data/api/credit-api/credit-api'
import {
  useGetPersonalFunding,
  usePostPersonalLoanWorksheet,
  usePutPersonalLoanWorksheet,
} from '@src/data/api/worksheet-api/personal-loan-worksheet-api'
import { useGetPossibleTerms } from '@src/data/api/worksheet-api/worksheet-api'
import { useWorksheetFormDefaultsSeed } from '@src/data/api/worksheet-api/worksheet-form-defaults-api'
import {
  createNewPersonalLoanWorksheetDto,
  getPersonalFundingDto,
  isVariableInterest,
  updatePersonalLoanWorksheet,
} from '@src/data/worksheet-selectors'
import useFeatureFlags from '@src/hooks/config-hooks'
import { Conflict } from '@src/types/Conflict'
import { Constants, FinancingProgramConfigs } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { UserDtoFromJwt } from '@src/types/User'
import { buildEditPersonalLoanWorksheetDtoSchema, EditPersonalLoanWorksheetDto } from '@src/types/WorksheetSchema'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  useLoanTermSelection,
  usePaymentMerchantList,
  useTermAndPlanSelection,
} from '../ViewCreditApplicationPage/worksheet-hooks'
import HighEstimateDialog from './components/HighEstimateDialog'
import PersonalLoanFundingSummary from './components/PersonalLoanFundingSummary'
import WorksheetDetails from './components/worksheetDetails'

interface Props {
  merchant: Merchant
  creditApplication: CreditApplication
  user: UserDtoFromJwt
  merchantsListFromParentId: Merchant[]
  merchantPayments: { label: string; value: string; id: string }[]
}
const PersonalLoanWorksheet = ({
  merchant,
  creditApplication,
  user,
  merchantsListFromParentId,
  merchantPayments,
}: Props) => {
  // Fetch merchant-specific financing config from backend
  const [merchantFinancingConfig, isLoadingConfig] = useMerchantFinancingConfig(
    merchant?.id,
    EFinancingProgram.Personal,
  )
  const isMedicardServiceCategory =
    merchant?.serviceCategory === ELoanPurpose.Medical ||
    merchant?.serviceCategory === ELoanPurpose.Dentistry ||
    merchant?.serviceCategory === ELoanPurpose.Aesthetics

  // Determine category-based max loan amount (50k for Medicard, 40k otherwise)
  const defaultConfig = FinancingProgramConfigs[EFinancingProgram.Personal]
  const categoryBasedMax = isMedicardServiceCategory ? defaultConfig.maxLoanAmountMedicard : defaultConfig.maxLoanAmount

  // Use the lower of category-based max and merchant config (if present)
  const maxLoanAmount = Math.min(categoryBasedMax, merchantFinancingConfig?.maximumLoanAmount ?? categoryBasedMax)
  const minLoanAmount = merchantFinancingConfig?.minimumLoanAmount ?? defaultConfig.minLoanAmount
  const maxTerm = merchantFinancingConfig?.maxLoanTerm ?? defaultConfig.maxTerm
  const minTerm = merchantFinancingConfig?.minLoanTerm ?? defaultConfig.minTerm

  // Build config override for schema
  const configOverride = {
    minLoanAmount,
    maxLoanAmount,
    maxTerm,
    minTerm,
  }

  const [validTermAndPlanKeys, setValidTermAndPlanKeys] = useState<Set<string>>(new Set())

  const { flags } = useFeatureFlags()
  const isBuydownEnabled = !!flags.UseCustomFlashPlan
  const { seedWorksheetFormDefaults } = useWorksheetFormDefaultsSeed()

  const EditPersonalLoanWorksheetDtoSchema = buildEditPersonalLoanWorksheetDtoSchema(
    creditApplication.applicant.hardHitReportReceivedOn!,
    creditApplication.finalCreditDecision.maxAmountFinanced!,
    creditApplication.merchantPaymentPlanId,
    configOverride,
    validTermAndPlanKeys,
  )

  const defaultValues =
    creditApplication.worksheet || !isBuydownEnabled || !creditApplication.id
      ? (creditApplication.worksheet ?? EditPersonalLoanWorksheetDtoSchema.getDefault())
      : async () => {
          const seed = EditPersonalLoanWorksheetDtoSchema.getDefault() as EditPersonalLoanWorksheetDto
          const defaults = await seedWorksheetFormDefaults({
            creditApplicationId: creditApplication.id!,
            originSystemId: EOriginSystemId.MerchantDashboard,
          })
          return { ...seed, amountRequested: defaults.amountRequested }
        }

  const formMethods = useForm<EditPersonalLoanWorksheetDto>({
    mode: 'onBlur',
    defaultValues: defaultValues as unknown as EditPersonalLoanWorksheetDto,
    resolver: yupResolver(EditPersonalLoanWorksheetDtoSchema),
  })

  const { setValue, watch, handleSubmit, control, trigger, formState } = formMethods
  const [postWorksheet, isPosting, isPostError, postError, resetPost] = usePostPersonalLoanWorksheet()
  const [putWorksheet, isPuting, isPutError, updateError, resetPut] = usePutPersonalLoanWorksheet()
  const [openUserDialog, setOpenUserDialog] = useState<boolean>(false)
  const [openInsuranceInfoBox, setOpenInsuranceInfoBox] = useState(false)
  const deliveryOn = watch('deliveryOn') as string | null
  useEffect(() => {
    void trigger('deliveryOn')
  }, [trigger])
  const amountRequested = normalizeNumber(watch('amountRequested')?.toString()) ?? 0
  const [debouncedAmountRequested, setDebouncedAmountRequested] = useState(amountRequested)
  const debouncedSetAmountRequested = useMemo(
    () => debounce((value: number) => setDebouncedAmountRequested(value), Constants.AmountRequestedDebounceMs),
    [],
  )
  useEffect(() => {
    debouncedSetAmountRequested(amountRequested)
    return () => {
      debouncedSetAmountRequested.clear()
    }
  }, [amountRequested, debouncedSetAmountRequested])
  const paymentFrequency = watch('paymentFrequency') ?? 'monthly'
  const term = watch('term') as number | null
  const firstPaymentOn = watch('firstPaymentOn') as string | null
  const paymentPlanId = watch('paymentPlanId')
  const includeInsurance = watch('includeInsurance')
  const isHighEstimate = watch('isHighEstimate')
  const merchantPaymentsValues = watch('merchantPayments')
  const totalMerchantPayment = merchantPaymentsValues?.reduce((acc, curr) => Number(acc) + Number(curr.amount ?? 0), 0)
  const hasVariableInterest = isVariableInterest(paymentPlanId)
  const [error, setError] = useState<Error | null>(null)

  const { merchantList, setMerchantList } = usePaymentMerchantList(
    creditApplication,
    merchantsListFromParentId,
    merchant,
    user,
  )
  const hasCVT = Boolean(creditApplication.contract.cvtNumber)
  const [highEstimateShouldDisableFields, setHighEstimateShouldDisableFields] = useState(
    hasCVT && merchant.canAcceptHighEstimate,
  )
  const navigate = useNavigate()
  const { t } = useTranslation()
  const conflict = postError
    ? (postError?.response?.data as unknown as Conflict)
    : (updateError?.response?.data as unknown as Conflict)

  const dto = useMemo(() => {
    if (deliveryOn && firstPaymentOn && debouncedAmountRequested && term) {
      return getPersonalFundingDto(
        creditApplication.id!,
        debouncedAmountRequested,
        paymentFrequency,
        term,
        deliveryOn,
        firstPaymentOn,
        includeInsurance,
        creditApplication.applicant.currentAddress.stateIso,
        paymentPlanId,
        creditApplication.finalCreditDecision.interestRate,
        creditApplication.coApplicant !== null,
      )
    }
    return null
  }, [
    creditApplication,
    debouncedAmountRequested,
    deliveryOn,
    firstPaymentOn,
    includeInsurance,
    paymentFrequency,
    paymentPlanId,
    term,
  ])

  const computeMonthTermFrequency = useMemo(() => {
    if (!isBuydownEnabled && deliveryOn && firstPaymentOn && debouncedAmountRequested) {
      return {
        merchantId: creditApplication.merchantId,
        paymentPlanId,
        interestRate: creditApplication.finalCreditDecision.interestRate,
        hasCoapplicant: creditApplication.coApplicant !== null,
        stateIso: creditApplication.applicant.currentAddress.stateIso!,
        paymentFrequency,
        deliveryOn,
        firstPaymentOn,
        maxPmtAmount: creditApplication.finalCreditDecision.maxPmtAmount,
        amountRequested: debouncedAmountRequested,
        includeInsurance,
        include84Months: (creditApplication.finalCreditDecision.maxAmountFinanced ?? 0) >= Constants.LargeLoanAmount,
      }
    }
    return null
  }, [
    isBuydownEnabled,
    creditApplication.merchantId,
    paymentPlanId,
    creditApplication.finalCreditDecision.interestRate,
    creditApplication.coApplicant,
    creditApplication.applicant.currentAddress.stateIso,
    paymentFrequency,
    deliveryOn,
    firstPaymentOn,
    creditApplication.finalCreditDecision.maxPmtAmount,
    creditApplication.finalCreditDecision.maxAmountFinanced,
    debouncedAmountRequested,
    includeInsurance,
  ])

  const [possibleTerms, isLoadingTerms] = useGetPossibleTerms(
    computeMonthTermFrequency,
    creditApplication.finalCreditDecision.maxAmountFinanced!,
  )
  const { loanTermChoicesList, handlePaymentPlanChange } = useLoanTermSelection({
    possibleTerms,
    computeMonthTermFrequency,
    creditApplication,
    merchant,
    paymentPlanId,
    term,
    regularMinTerm: minTerm,
    setValue,
  })

  const {
    options: termAndPlanOptions,
    hasPromoRows,
    isLoading: isLoadingTermAndPlanOptions,
    selectedOption: selectedTermAndPlanOption,
  } = useTermAndPlanSelection({
    isBuydownEnabled,
    creditApplicationId: creditApplication.id,
    merchantId: creditApplication.merchantId,
    interestRate: creditApplication.finalCreditDecision.interestRate,
    hasCoapplicant: creditApplication.coApplicant !== null,
    stateIso: creditApplication.applicant.currentAddress.stateIso,
    paymentFrequency,
    deliveryOn,
    firstPaymentOn,
    maxPmtAmount: creditApplication.finalCreditDecision.maxPmtAmount,
    amountRequested: debouncedAmountRequested,
    includeInsurance,
    paymentPlanId,
    term,
  })

  useEffect(() => {
    setValidTermAndPlanKeys(
      isBuydownEnabled ? new Set(termAndPlanOptions.filter((o) => !o.disabled).map((o) => o.key)) : new Set(),
    )
  }, [isBuydownEnabled, termAndPlanOptions])

  const [computedPersonalLoanFunding, isComputing] = useGetPersonalFunding(
    creditApplication.id!,
    dto,
    creditApplication.finalCreditDecision.maxAmountFinanced!,
  )
  const onClosedMerchantDialog = useCallback(() => {
    setOpenUserDialog(false)
  }, [])
  const doesTermSupportInsurance = useMemo(() => {
    if (isBuydownEnabled) return true
    if (!possibleTerms?.monthlyTerms) return false
    const matchingTerm = possibleTerms.monthlyTerms.find((item) => item.term === Number(term))

    return matchingTerm ? matchingTerm.effectiveRateWithInsuranceIsLegal : false
  }, [isBuydownEnabled, possibleTerms, term])

  const resetPayments = useCallback(() => {
    merchantList.forEach((_merchant, index) => {
      setValue(`merchantPayments.${index}.amount`, 0)
    })
  }, [merchantList, setValue])

  useEffect(() => {
    if (
      term === Constants.LongTerm &&
      (creditApplication?.finalCreditDecision.maxAmountFinanced ?? 0) < Constants.LargeLoanAmount &&
      creditApplication?.finalCreditDecision.maxTermDuration
    )
      setValue('term', creditApplication?.finalCreditDecision.maxTermDuration)
  }, [
    creditApplication?.finalCreditDecision.maxAmountFinanced,
    term,
    creditApplication?.finalCreditDecision.maxTermDuration,
    setValue,
  ])

  useEffect(() => {
    if (paymentPlanId !== EPaymentPlan.RegularDailyInterests) {
      setValue('isHighEstimate', false)
    }
  }, [paymentPlanId, setValue])

  const onMerchantsSelected = useCallback(
    (newMerchants: Merchant[]) => {
      setOpenUserDialog(false)
      resetPayments()
      setMerchantList(newMerchants)
    },
    [resetPayments, setMerchantList],
  )

  const navigateToView = (financingProgramId: string, id: string) => {
    navigate(`/credits/${financingProgramId}/${id}`)
  }
  const [openHighEstimateConfirmation, setOpenHighEstimateConfirmation] = useState(false)
  const [highEstimateConfirmationChecked, setHighEstimateConfirmationChecked] = useState(false)

  const handleHighEstimateConfirmationClose = () => {
    setOpenHighEstimateConfirmation(false)
    setHighEstimateConfirmationChecked(false)
  }

  const handleHighEstimateConfirmationConfirm = async (formData: EditPersonalLoanWorksheetDto) => {
    handleHighEstimateConfirmationClose()
    await saveWorksheet(formData)
  }
  const saveWorksheet = async (formData: EditPersonalLoanWorksheetDto) => {
    merchantList.forEach((merch, index) => {
      formData.merchantPayments[index].merchantId = merch.id
      formData.merchantPayments[index].paymentMethod = merch.defaultPaymentMethod
    })
    if (creditApplication.worksheet == null) {
      await postWorksheet({ worksheet: createNewPersonalLoanWorksheetDto(creditApplication.id!, formData) }).then(
        () => {
          navigateToView(creditApplication.financingProgramId, creditApplication.id!)
        },
      )
    } else {
      await putWorksheet({
        worksheet: updatePersonalLoanWorksheet(formData, creditApplication),
      }).then(() => {
        navigateToView(creditApplication.financingProgramId, creditApplication.id!)
      })
    }
  }
  const onSubmit = async (formData: EditPersonalLoanWorksheetDto) => {
    if (formData.isHighEstimate) {
      setOpenHighEstimateConfirmation(true)
    } else {
      await saveWorksheet(formData)
    }
  }

  useEffect(() => {
    if (!user.isBannerUser && amountRequested) setValue(`merchantPayments.0.amount`, amountRequested)
  }, [amountRequested, setValue, user.isBannerUser])

  useEffect(() => {
    if (totalMerchantPayment !== undefined && merchantPaymentsValues?.[0]?.amount !== undefined) {
      void trigger('amountRequested')
    }
  }, [merchantPaymentsValues, totalMerchantPayment, trigger])

  const isAPRTooHigh = useMemo(() => {
    const address = creditApplication.applicant.currentAddress
    if (!address || !computedPersonalLoanFunding) return false

    const { effectiveRate } = computedPersonalLoanFunding
    const { stateIso } = address

    const aprThresholds: Record<string, number> = {
      [EProvince.Newfoundland]: 27,
    }

    const threshold = aprThresholds[stateIso!]

    return threshold !== undefined && effectiveRate >= threshold
  }, [computedPersonalLoanFunding, creditApplication.applicant.currentAddress])

  const firstPaymentDateOptions = useGetFirstPaymentDateOptions(
    creditApplication.id,
    creditApplication.financingProgramId,
    {
      deliveryOn: deliveryOn!,
      systemOriginId: EOriginSystemId.MerchantDashboard,
      isHighEstimate: false,
      paymentFrequency: paymentFrequency,
    },
  )

  useEffect(() => {
    const currentFirstPaymentDateAsString = formatDate(firstPaymentOn)
    const availableDatesAsString: (string | null)[] = []
    firstPaymentDateOptions.forEach((item) => {
      availableDatesAsString.push(formatDate(item))
    })

    if (availableDatesAsString.length > 0 && !availableDatesAsString.includes(currentFirstPaymentDateAsString)) {
      setValue('firstPaymentOn', formatDate(firstPaymentDateOptions[0]), {})
    }
  }, [setValue, firstPaymentOn, firstPaymentDateOptions])

  const hasConflict = updateError?.response?.status === 409 || postError?.response?.status === 409
  const handleClose = useCallback(() => {
    if (updateError?.response?.status === 409) {
      resetPut()
    }
    if (postError?.response?.status === 409) {
      resetPost()
    }
  }, [resetPost, resetPut, updateError, postError])

  useEffect(() => {
    if (
      computeMonthTermFrequency?.maxPmtAmount &&
      computedPersonalLoanFunding?.paymentForFrequency > computeMonthTermFrequency?.maxPmtAmount
    ) {
      setError(new Error(t('common.errors.maxpaymentExceedsMaximumAllowed')))
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    setError(null)
  }, [computedPersonalLoanFunding?.paymentForFrequency, computeMonthTermFrequency?.maxPmtAmount, t])

  if (isLoadingConfig || formState.isLoading) {
    return <PageSpinner isLoading />
  }

  return (
    <FormProvider {...formMethods}>
      <div>
        {/* Conditional Banner */}
        {firstPaymentOn && isHighEstimate && (
          <Alert severity="warning" sx={{ mb: 2 }} icon={<Alarm />}>
            <Typography sx={{ whiteSpace: 'pre-line' }}>
              {t('worksheet.highEstimateInvoiceWarning', {
                firstPaymentOn: formatDate(firstPaymentOn),
                invoiceDeadline: formatDate(addDays(new Date(firstPaymentOn), -5)),
              })}
            </Typography>
          </Alert>
        )}
        {(postError || updateError) && !hasConflict && (
          <Paper>
            <Alert severity="error">
              <Typography>{postError?.response?.data?.message || updateError?.response?.data?.message}</Typography>
            </Alert>
          </Paper>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{
              width: '100%',
              marginBottom: '2rem',
            }}
          >
            <AlertTitle>{t('common.error')}</AlertTitle>
            {error.message}
          </Alert>
        )}
        {hasConflict && <ConflictDialog updatedOn={conflict?.updatedOn} open={hasConflict} onClose={handleClose} />}
        <form onSubmit={handleSubmit(onSubmit)}>
          <FieldReadyGateProvider>
            <Stack direction={{ md: 'column', xs: 'column-reverse' }} spacing={5} justifyContent="space-between">
              <Stack
                direction={{ sm: 'row', xs: 'column' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ sm: 'self-end', xs: 'self-start' }}
              >
                <Stack direction="column" justifyContent="space-between">
                  <Typography variant="h4">{t('worksheet.worksheet')}</Typography>
                  <Typography variant="h5">
                    {t('worksheet.maxLoanAmount')}:{' '}
                    {formatCurrency(creditApplication.finalCreditDecision.maxAmountFinanced)}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} display="flex">
                  <AsyncActionButton fullWidth={false} variant="contained" onClick={() => navigate(-1)}>
                    {t('common.previous')}
                  </AsyncActionButton>

                  {!highEstimateShouldDisableFields && (
                    <AsyncActionButton
                      fullWidth={false}
                      variant="contained"
                      disabled={isAPRTooHigh || isComputing || !computedPersonalLoanFunding || error != null}
                      isPending={isPosting || isPuting}
                      isError={isPostError || isPutError}
                      onClick={handleSubmit(onSubmit)}
                    >
                      {t('worksheet.createContract')}
                    </AsyncActionButton>
                  )}
                  {highEstimateShouldDisableFields && (
                    <div>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={isAPRTooHigh || isComputing || !computedPersonalLoanFunding}
                        sx={{ width: 'auto', whiteSpace: 'nowrap', textAlign: 'left' }}
                        onClick={() => setHighEstimateShouldDisableFields(false)}
                        startIcon={<Lock />}
                      >
                        <div>
                          <Typography variant="body1" component="div">
                            {t('worksheet.unlockToModify')}
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ fontSize: '0.75rem' }}>
                            {t('worksheet.willCancelContract')}
                          </Typography>
                        </div>
                      </Button>
                    </div>
                  )}
                </Stack>
              </Stack>

              <Stack direction={{ md: 'row', xs: 'column-reverse' }} spacing={5} justifyContent="space-between">
                <Box sx={{ flex: 1 }}>
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <WorksheetDetails
                        creditApplication={creditApplication}
                        merchantPayments={merchantPayments}
                        loanTermChoicesList={loanTermChoicesList}
                        isLoadingTerm={isLoadingTerms}
                        planPromoRules={merchant.planPromoRules}
                        firstPaymentDateOptions={firstPaymentDateOptions}
                        onPaymentPlanChange={handlePaymentPlanChange}
                        isBuydownEnabled={isBuydownEnabled}
                        termAndPlanOptions={termAndPlanOptions}
                        hasPromoRows={hasPromoRows}
                        isLoadingTermAndPlanOptions={isLoadingTermAndPlanOptions}
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                        md: 4,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ControlledCheckbox
                          name="includeInsurance"
                          control={control}
                          label={t('worksheet.addProtectionPlan')}
                          disabled={!doesTermSupportInsurance}
                        />
                        <Tooltip title={t('worksheet.includeProtectionPlan')}>
                          <IconButton onClick={() => setOpenInsuranceInfoBox((prev) => !prev)}>
                            <InfoIcon style={{ fontSize: 'medium' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6,
                        md: 4,
                      }}
                    >
                      {merchant?.canAcceptHighEstimate && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ControlledCheckbox
                            name="isHighEstimate"
                            control={control}
                            label={t('worksheet.highEstimate')}
                            disabled={paymentPlanId !== EPaymentPlan.RegularDailyInterests}
                          />
                          <Tooltip title={t('worksheet.highEstimateTooltip')}>
                            <IconButton>
                              <InfoIcon style={{ fontSize: 'medium' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Grid>
                    {openInsuranceInfoBox && (
                      <Box component="div" sx={{ padding: '10px', marginLeft: '15px' }}>
                        <Typography variant="h5" sx={{ marginBottom: '10px' }}>
                          {t('worksheet.insuranceIncitative')}
                        </Typography>

                        <ul style={{ paddingLeft: '20px' }}>
                          <li>
                            <Typography variant="body1" sx={{ marginBottom: '10px' }}>
                              {t('worksheet.lifeInsurance')}
                            </Typography>
                          </li>
                          <li>
                            <Typography variant="body1" sx={{ marginBottom: '10px' }}>
                              {t('worksheet.accidentalDisability')}
                            </Typography>
                          </li>
                        </ul>
                      </Box>
                    )}
                    <Grid size={12}>
                      <Stack sx={{ mb: 2 }} direction="row" alignItems="center">
                        <Typography variant="h5" sx={{ flexGrow: 1 }}>
                          {t('worksheet.paymentBreakdown')}
                        </Typography>
                        {user.isBannerUser && (
                          <>
                            <AsyncActionButton
                              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                              fullWidth={false}
                              color="secondary"
                              variant="contained"
                              onClick={() => setOpenUserDialog(true)}
                            >
                              {t('worksheet.addMerchantPayment')}
                            </AsyncActionButton>
                            <Tooltip title={t('worksheet.addMerchantPayment')}>
                              <IconButton
                                onClick={() => setOpenUserDialog(true)}
                                color="secondary"
                                sx={{ display: { md: 'none' } }}
                              >
                                <AddIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                      <HighEstimateDialog
                        open={openHighEstimateConfirmation}
                        checked={highEstimateConfirmationChecked}
                        onCheckedChange={(checked) => {
                          setHighEstimateConfirmationChecked(checked)
                          setValue('highEstimateExplainedToClient', checked)
                        }}
                        onClose={handleHighEstimateConfirmationClose}
                        onConfirm={handleSubmit(handleHighEstimateConfirmationConfirm)}
                      />
                      <Stack spacing={2}>
                        {merchantList?.map((m, index) => {
                          return (
                            <div key={m.id}>
                              <Grid container spacing={1}>
                                <Grid
                                  size={{
                                    xs: 12,
                                    sm: 8,
                                  }}
                                >
                                  <Typography sx={{ mt: { sm: 2 } }}>{m.name}</Typography>
                                </Grid>
                                <Grid
                                  size={{
                                    xs: 12,
                                    sm: 4,
                                  }}
                                >
                                  <ControlledNumberField
                                    name={`merchantPayments.${index}.amount`}
                                    control={control}
                                    disabled={!user.isBannerUser}
                                    label={t('worksheet.paymentAmount')}
                                    isMoney
                                  />
                                </Grid>
                              </Grid>
                            </div>
                          )
                        })}
                      </Stack>
                    </Grid>
                    <Grid container sx={{ display: { md: 'none' } }} size={12}>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <AsyncActionButton color="error" onClick={() => navigate(-1)}>
                          {t('common.previous')}
                        </AsyncActionButton>
                      </Grid>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 6,
                        }}
                      >
                        <AsyncActionButton
                          variant="contained"
                          color="primary"
                          disabled={isAPRTooHigh || isComputing || !computedPersonalLoanFunding}
                          isPending={isPosting || isPuting}
                          isError={isPostError || isPutError}
                          onClick={handleSubmit(onSubmit)}
                        >
                          {t('worksheet.createContract')}
                        </AsyncActionButton>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
                <Box
                  sx={{
                    border: 1,
                    padding: 2,
                    maxWidth: { xs: '100%', md: '350px' },
                  }}
                >
                  <PersonalLoanFundingSummary
                    creditApplication={creditApplication}
                    amountRequested={amountRequested}
                    hasVariableInterest={hasVariableInterest}
                    firstPaymentOn={firstPaymentOn}
                    merchant={merchant}
                    paymentPlanId={paymentPlanId}
                    computedPersonalLoanFunding={computedPersonalLoanFunding}
                    isComputing={isComputing}
                    selectedTermAndPlanOption={selectedTermAndPlanOption}
                  />
                </Box>
              </Stack>
            </Stack>
          </FieldReadyGateProvider>
        </form>
        {user.isBannerUser && (
          <SelectMerchantsDialog
            open={openUserDialog}
            title={t('worksheet.addMerchantPayment')}
            label={t('common.merchant')}
            merchantsTotal={merchantsListFromParentId}
            merchantsSelected={merchantList}
            onConfirm={onMerchantsSelected}
            onCancel={onClosedMerchantDialog}
          />
        )}
      </div>
    </FormProvider>
  )
}

export default PersonalLoanWorksheet
